import { formatDay } from './areaTrend';

export const AREA_PERIODS = [
  { id: 'month_to_date', label: 'Month to date' },
  { id: 'week_to_date', label: 'Week to date' },
  { id: 'quarter_to_date', label: 'Quarter to date' },
  { id: 'year_to_date', label: 'Year to date' },
  { id: 'last_28_days', label: 'Last 28 days' },
] as const;

export type AreaPeriod = (typeof AREA_PERIODS)[number]['id'];

export const DEFAULT_AREA_PERIOD: AreaPeriod = 'month_to_date';

/** A preset, or a custom range with both ends as `YYYY-MM-DD`. */
export type AreaPeriodSelection = { period: AreaPeriod } | { period: 'custom'; from: string; to: string };

export const DEFAULT_AREA_SELECTION: AreaPeriodSelection = { period: DEFAULT_AREA_PERIOD };

/** Page-owned query params, so the shell carries them from Home to an area page and back. */
export const AREA_PERIOD_PARAMS = { from: 'pulseFrom', period: 'pulsePeriod', to: 'pulseTo' } as const;

/** The widest range the snapshot endpoints accept, as CustomRangeDialog enforces. */
export const CUSTOM_RANGE_MAX_DAYS = 366;

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

export const isAreaPeriod = (value: string | null | undefined): value is AreaPeriod =>
  AREA_PERIODS.some((period) => period.id === value);

export const areaPeriodLabel = (period: AreaPeriod) =>
  AREA_PERIODS.find((option) => option.id === period)?.label ?? period;

const utcDay = (value: string) => Date.parse(`${value}T00:00:00Z`);

/** True for a real calendar day in `YYYY-MM-DD` form. */
function isDay(value: string | null | undefined): value is string {
  return value != null && DAY.test(value) && new Date(utcDay(value)).toISOString().startsWith(value);
}

/** A valid range: real days, start not after end, end not after `maxDate`, at most 366 days. */
export function isValidCustomRange(from: string | null, to: string | null, maxDate?: string): boolean {
  if (!isDay(from) || !isDay(to) || from > to || (maxDate && to > maxDate)) {
    return false;
  }
  return (utcDay(to) - utcDay(from)) / DAY_MS + 1 <= CUSTOM_RANGE_MAX_DAYS;
}

/** The selection a URL describes. A custom range with a missing or invalid end falls back to the default. */
export function parseAreaPeriod(search: string): AreaPeriodSelection {
  const params = new URLSearchParams(search);
  const period = params.get(AREA_PERIOD_PARAMS.period);
  if (period === 'custom') {
    const from = params.get(AREA_PERIOD_PARAMS.from);
    const to = params.get(AREA_PERIOD_PARAMS.to);
    return isValidCustomRange(from, to) ? { from: from as string, period, to: to as string } : DEFAULT_AREA_SELECTION;
  }
  return isAreaPeriod(period) ? { period } : DEFAULT_AREA_SELECTION;
}

/** Writes the selection into `params`, leaving the default and any custom-only params out. */
export function writeAreaPeriod(params: URLSearchParams, selection: AreaPeriodSelection): void {
  params.delete(AREA_PERIOD_PARAMS.period);
  params.delete(AREA_PERIOD_PARAMS.from);
  params.delete(AREA_PERIOD_PARAMS.to);
  if (selection.period === 'custom') {
    params.set(AREA_PERIOD_PARAMS.period, 'custom');
    params.set(AREA_PERIOD_PARAMS.from, selection.from);
    params.set(AREA_PERIOD_PARAMS.to, selection.to);
  } else if (selection.period !== DEFAULT_AREA_PERIOD) {
    params.set(AREA_PERIOD_PARAMS.period, selection.period);
  }
}

/** "1 Oct to 15 Oct 2026", or "15 Oct 2026" for one day. The year shows once unless the ends differ in year. */
export function customRangeLabel(from: string, to: string): string {
  if (from === to) {
    return formatDay(to);
  }
  return `${formatDay(from, from.slice(0, 4) !== to.slice(0, 4))} to ${formatDay(to)}`;
}

/** The earliest of the loaded snapshots' last complete days: the latest date every area can answer for. */
export function earliestDataTo(dates: ReadonlyArray<string | null | undefined>): string | undefined {
  const days = dates.map((date) => date?.slice(0, 10)).filter(isDay);
  return days.length ? days.reduce((earliest, day) => (day < earliest ? day : earliest)) : undefined;
}
